import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import { findOrThrow, loadErrorMessage } from "shared/loadOrError";
import type {
  AnalysisQueueMeeting,
  AnalysisQueuePage,
} from "../meetingIntelligence.types";

import { isSessionEnded, sessionMark } from "stores/sessionGuard";
/**
 * The handoff to Gantry, from HQ's side.
 *
 * ── WHAT THIS SCREEN IS FOR ────────────────────────────────────────────────
 *
 * Meetings arrive by webhook and a producer (Gantry) pulls them, analyses them,
 * and pushes back action items, decisions and a summary. Until now none of that
 * exchange was visible from inside HQ: an operator could see the meetings and
 * could see the results, and had no way to see the QUEUE BETWEEN THEM — what is
 * waiting, what a producer is holding, or whether anything is moving at all.
 *
 * ── THE THREE STATES THIS MUST NOT COLLAPSE ────────────────────────────────
 *
 * They render almost identically and mean opposite things:
 *
 *   "the queue is empty"        nothing to analyse. The ordinary healthy state.
 *   "HQ could not be reached"   the request failed. We know nothing.
 *   "nothing has ever loaded"   we have not asked yet.
 *
 * `error` and `loaded` are tracked separately from `page` for exactly this, and
 * the body renders three different things. A bare count would make all three
 * look like zero.
 */
export default class AnalysisQueueStore {
  page: AnalysisQueuePage | null = null;
  loading = false;
  /** Set when the load FAILED. Distinct from an empty queue. */
  error: string | null = null;
  /** False until a load has completed once, successfully or not. */
  loaded = false;
  /** Meeting id currently being requeued, so one row can show a spinner. */
  requeueingId: number | null = null;
  actionMessage: string | null = null;

  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  /**
   * Resolved through the manager first, exactly as the other queue stores do —
   * a service path is not a property name on the root store, and reading it as
   * one silently yields undefined rather than failing.
   */
  private get queueService(): ServiceStoreType | null {
    const root = this.rootStore as unknown as {
      serviceStoreManager?: { getStore: (p: string) => unknown };
    } & Record<string, unknown>;
    return (
      ((root.serviceStoreManager?.getStore("meeting-analysis-queue") ??
        root["meeting-analysis-queue"]) as ServiceStoreType | null) ?? null
    );
  }

  /** The meetings, oldest first — the order a producer is handed them. */
  get meetings(): AnalysisQueueMeeting[] {
    return this.page?.meetings ?? [];
  }

  /**
   * Total waiting, or NULL when we do not know.
   *
   * Null is not zero. A failed load and an empty queue must never render the
   * same, so the caller has to handle the null rather than being handed a
   * plausible-looking number.
   */
  get waiting(): number | null {
    return this.page ? this.page.total : null;
  }

  /**
   * Can HQ serve Read.ai sources to a producer at all?
   *
   * Config-level, so it answers "is the Read.ai API set up" rather than
   * anything about a specific meeting. Null when we have not loaded.
   */
  get sources(): { transcript: boolean; recording: boolean } | null {
    return this.page?.readAiSources ?? null;
  }

  /**
   * Rows a producer will be OFFERED but that are not meetings.
   *
   * `email` is an uploaded thread. It should not be in the queue at all — the
   * fix that keeps them out ships separately — so seeing any here means this
   * instance predates it, and a producer is being handed non-meetings.
   */
  get nonMeetings(): AnalysisQueueMeeting[] {
    return this.meetings.filter((m) => m.sourceType === "email");
  }

  /** How many rows a producer is holding right now. */
  get claimed(): number {
    return this.meetings.filter((m) => m.analysisState === "claimed").length;
  }

  async load(): Promise<void> {
    const service = this.queueService;
    if (!service) {
      runInAction(() => {
        this.error = "The analysis queue service is not registered in this app.";
        this.loaded = true;
      });
      return;
    }
    const ended = sessionMark(this.rootStore);
    runInAction(() => {
      this.loading = true;
      this.error = null;
    });
    try {
      // The queue answers with a page object, not an array — `find` returns
      // { meetings, total, readAiSources }.
      // findOrThrow: the library ServiceStore RESOLVES null on a failed find
      // (recording errorOnFind), which the fallback below turned into an empty
      // page — "Nothing is waiting" with 0 waiting, on a failed read.
      const res = (await findOrThrow(service, { query: { $limit: 25 } })) as unknown as
        | AnalysisQueuePage
        | undefined;
      // Signed out meanwhile: this answer is the previous person's, and the
      // `loaded` latch below must not make an emptied page read as "all clear".
      if (ended()) return;
      runInAction(() => {
        this.page =
          res && Array.isArray(res.meetings)
            ? res
            : { meetings: [], total: 0, readAiSources: { transcript: false, recording: false } };
      });
    } catch (err) {
      // A failure leaves `page` ALONE rather than blanking it to an empty
      // object: showing "0 waiting" because a request failed is the exact
      // confidently-wrong signal this screen exists to avoid.
      if (ended()) return;
      runInAction(() => {
        this.error = loadErrorMessage(err, "Could not reach HQ's analysis queue.");
      });
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.loading = false;
          this.loaded = true;
        });
      }
    }
  }

  /**
   * Send one meeting back to the queue.
   *
   * The API accepts `requeue` for a `failed` or `skipped` row only, and is a
   * no-op on anything else — so this is safe to offer beside any row, and the
   * reply says whether it actually moved.
   */
  async requeue(meetingId: number): Promise<void> {
    const service = this.queueService;
    if (!service) return;
    runInAction(() => {
      this.requeueingId = meetingId;
      this.actionMessage = null;
    });
    try {
      const res = (await service.patch(meetingId, { action: "requeue" })) as unknown as
        | { requeued?: boolean }
        | undefined;
      runInAction(() => {
        // `requeued: false` is not an error — it means the row was not in a
        // state that can be requeued. Say which, rather than implying success.
        this.actionMessage = res?.requeued
          ? `Meeting ${meetingId} was sent back to the queue.`
          : `Meeting ${meetingId} was not requeued — only failed or skipped meetings can be.`;
      });
      await this.load();
    } catch (err) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.actionMessage = (err as Error)?.message || "The requeue failed.";
      });
    } finally {
      runInAction(() => {
        this.requeueingId = null;
      });
    }
  }

  dismissMessage(): void {
    this.actionMessage = null;
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.page = null;
    this.loading = false;
    this.error = null;
    this.loaded = false;
    this.requeueingId = null;
    this.actionMessage = null;
  }
}
